import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { createUISlice, UISlice } from './slices/uiSlice';
import { createLeadsSlice, LeadsSlice } from './slices/leadsSlice';
import { createDealsSlice, DealsSlice } from './slices/dealsSlice';
import { createClientsSlice, ClientsSlice } from './slices/clientsSlice';
import { createPipelinesSlice, PipelinesSlice } from './slices/pipelinesSlice';

// Объединенный тип store
type StoreState = UISlice & LeadsSlice & DealsSlice & ClientsSlice & PipelinesSlice;

// Создаем главный store
export const useStore = create<StoreState>()(
  devtools(
    (...a) => ({
      ...createUISlice(...a),
      ...createLeadsSlice(...a),
      ...createDealsSlice(...a),
      ...createClientsSlice(...a),
      ...createPipelinesSlice(...a),
    }),
    { name: 'sphere-crm' }
  )
);

// Селекторы для оптимизации ре-рендеров
// Используйте эти селекторы в компонентах вместо useStore напрямую

const leadsSelector = (state: StoreState) => ({
  leads: state.leads,
  loading: state.leadsLoading,
  error: state.leadsError,
  pagination: state.leadsPagination,
  filters: state.leadsFilters,
  fetchLeads: state.fetchLeads,
  createLead: state.createLead,
  updateLead: state.updateLead,
  deleteLead: state.deleteLead,
  setLeadsFilters: state.setLeadsFilters,
  setLeadsPage: state.setLeadsPage,
});

const dealsSelector = (state: StoreState) => ({
  deals: state.deals,
  loading: state.dealsLoading,
  error: state.dealsError,
  pagination: state.dealsPagination,
  filters: state.dealsFilters,
  fetchDeals: state.fetchDeals,
  createDeal: state.createDeal,
  updateDeal: state.updateDeal,
  deleteDeal: state.deleteDeal,
  setDealsFilters: state.setDealsFilters,
  setDealsPage: state.setDealsPage,
});

const clientsSelector = (state: StoreState) => ({
  clients: state.clients,
  loading: state.clientsLoading,
  error: state.clientsError,
  pagination: state.clientsPagination,
  filters: state.clientsFilters,
  fetchClients: state.fetchClients,
  createClient: state.createClient,
  updateClient: state.updateClient,
  deleteClient: state.deleteClient,
  setClientsFilters: state.setClientsFilters,
  setClientsPage: state.setClientsPage,
});

const pipelinesSelector = (state: StoreState) => ({
  pipelines: state.pipelines,
  currentPipeline: state.currentPipeline,
  loading: state.pipelinesLoading,
  error: state.pipelinesError,
  fetchPipelines: state.fetchPipelines,
  fetchPipeline: state.fetchPipeline,
  createPipeline: state.createPipeline,
  updatePipeline: state.updatePipeline,
  deletePipeline: state.deletePipeline,
  addStage: state.addStage,
  removeStage: state.removeStage,
  setCurrentPipeline: state.setCurrentPipeline,
});

const uiSelector = (state: StoreState) => ({
  editingItems: state.editingItems,
  notifications: state.notifications,
  cacheTimestamps: state.cacheTimestamps,
  startEditing: state.startEditing,
  stopEditing: state.stopEditing,
  isEditing: state.isEditing,
  addNotification: state.addNotification,
  removeNotification: state.removeNotification,
  clearNotifications: state.clearNotifications,
  setCacheTimestamp: state.setCacheTimestamp,
  shouldRefetch: state.shouldRefetch,
});

// ✅ Правильный способ с useShallow - предотвращает бесконечные циклы
export const useLeads = () => useStore(useShallow(leadsSelector));
export const useDeals = () => useStore(useShallow(dealsSelector));
export const useClients = () => useStore(useShallow(clientsSelector));
export const usePipelines = () => useStore(useShallow(pipelinesSelector));
export const useUI = () => useStore(useShallow(uiSelector));

// Export типов для использования в компонентах
export type { Lead } from './slices/leadsSlice';
export type { Deal } from './slices/dealsSlice';
export type { Client } from './slices/clientsSlice';
export type { Pipeline, Stage } from './slices/pipelinesSlice';
export type { Notification } from './slices/uiSlice';
